/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import {
	type AnalyticsEventPayload,
	type UIAnalyticsEvent,
	type WithAnalyticsEventsProps,
} from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { Box, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import Button, { LinkButton } from '@atlaskit/button/new';
import React, { Fragment, useState, useEffect } from 'react';
import { injectIntl, type IntlShape } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import TRELLO_ERROR_SRC from '../../assets/trello-error';
import UNVERIFIED_ERROR_SRC from '../../assets/unverified-error';
import {
	NAVIGATION_CHANNEL,
	OPERATIONAL_EVENT_TYPE,
	UI_EVENT_TYPE,
	withAnalyticsEvents,
} from '../../common/utils/analytics';
import { getLoginUrl } from '../../common/utils/environment';
import { errorToReason, type Reason } from '../../common/utils/error-to-reason';
import messages from '../../common/utils/messages';
import { Product } from '../../types';
import { FormattedMessage } from 'react-intl-next';
import { SwitcherLoadError } from '../../common/utils/errors/switcher-load-error';
import { FETCH_ERROR_NAME } from '../../common/utils/errors/fetch-error';

const TRIGGER_SUBJECT = 'errorBoundary';
const ACTION_SUBJECT = 'rendered';
// This image is also used as the generic error message in Notifications

const robotsImgStyles = css({
	height: 200,
});

const trelloErrorImgStyles = css({
	width: 140,
	height: 88,
});

const unverifiedUserImageStyles = css({
	height: 150,
});

const errorContentStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexDirection: 'column',
});

const errorHeadingStyles = xcss({
	maxWidth: '240',
	marginTop: 'space.400',
});

const errorMessageStyles = xcss({
	maxWidth: '240',
	marginTop: 'space.300',
});

const errorBoundaryBoxStyles = css({
	display: 'flex',
	justifyContent: 'center',
	flexDirection: 'column',
	textAlign: 'center',
	margin: `${token('space.600', '48px')} 0`,
});

const loginButtonStyles = xcss({
	marginTop: 'space.300',
	justifyContent: 'center',
	alignSelf: 'stretch',
});

interface ErrorBoundaryProps {
	product: Product;
	children: React.ReactNode;
	onErrorCallback?: (error: Error) => void;
	triggerSubject?: string;
	hideFallbackUI?: boolean;
	extraAnalyticProps?: object;
	intl: IntlShape;
}

type ErrorBoundaryState = {
	hasError: boolean;
	reason?: Reason;
};

const ErrorMessage = (props: { children: React.ReactNode }) => (
	<Box xcss={errorMessageStyles}>
		<Text as="p">{props.children}</Text>
	</Box>
);

class ErrorBoundary extends React.Component<
	ErrorBoundaryProps & WithAnalyticsEventsProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = { hasError: false };

	fireOperationalEvent = (payload: AnalyticsEventPayload) => {
		if (this.props.createAnalyticsEvent) {
			this.props
				.createAnalyticsEvent({
					eventType: OPERATIONAL_EVENT_TYPE,
					actionSubject: this.props.triggerSubject || TRIGGER_SUBJECT,
					...payload,
				})
				.fire(NAVIGATION_CHANNEL);
		}
	};

	componentDidCatch(error: any) {
		const reason = error instanceof SwitcherLoadError ? error.reason() : errorToReason(error);

		this.setState(
			{
				hasError: true,
				reason,
			},
			() => {
				this.fireOperationalEvent({
					action: ACTION_SUBJECT,
					attributes: {
						...reason,
						...this.props.extraAnalyticProps,
					},
				});
			},
		);

		this.props.onErrorCallback?.(error);
	}

	handleLogin = (_: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		analyticsEvent
			.update({
				eventType: UI_EVENT_TYPE,
				actionSubjectId: 'login',
			})
			.fire(NAVIGATION_CHANNEL);

		if (this.props.product !== Product.TRELLO) {
			window.location.reload();
		}
	};

	renderErrorBody() {
		const { reason } = this.state;

		if (!reason) {
			return;
		}

		if (reason.name === FETCH_ERROR_NAME) {
			const isTrello = this.props.product === Product.TRELLO;

			if (reason.status === 401) {
				// Not authorised http error
				return (
					<Fragment>
						<ErrorMessage>
							<FormattedMessage {...messages.errorTextLoggedOut} />
						</ErrorMessage>
						{isTrello ? (
							<Box xcss={loginButtonStyles}>
								<LinkButton href={getLoginUrl(Product.TRELLO)} onClick={this.handleLogin}>
									<FormattedMessage {...messages.login} />
								</LinkButton>
							</Box>
						) : (
							<Box xcss={loginButtonStyles}>
								<Button onClick={this.handleLogin}>
									<FormattedMessage {...messages.login} />
								</Button>
							</Box>
						)}
					</Fragment>
				);
			}

			if (reason.status === 403) {
				return (
					<ErrorMessage>
						<FormattedMessage {...messages.errorTextUnverified} />
					</ErrorMessage>
				);
			}

			return (
				<ErrorMessage>
					<FormattedMessage {...messages.errorTextNetwork} />
				</ErrorMessage>
			);
		}

		// Default error message
		return (
			<ErrorMessage>
				<FormattedMessage {...messages.errorText} />
			</ErrorMessage>
		);
	}

	renderErrorHeading() {
		const { reason } = this.state;

		if (!reason) {
			return;
		}

		if (reason.name === FETCH_ERROR_NAME && reason.status === 401) {
			return <FormattedMessage {...messages.errorHeadingLoggedOut} />;
		}

		if (reason.name === FETCH_ERROR_NAME && reason.status === 403) {
			return <FormattedMessage {...messages.errorHeadingUnverified} />;
		}

		return <FormattedMessage {...messages.errorHeading} />;
	}

	renderErrorImage() {
		const { intl, product } = this.props;
		const { reason } = this.state;

		if (product === Product.TRELLO) {
			return (
				<img
					css={trelloErrorImgStyles}
					src={TRELLO_ERROR_SRC}
					alt={intl.formatMessage(messages.errorImageAltText)}
				/>
			);
		}

		if (reason!.status === 403) {
			return (
				<img
					css={unverifiedUserImageStyles}
					src={UNVERIFIED_ERROR_SRC}
					alt={intl.formatMessage(messages.errorImageAltText)}
				/>
			);
		}

		return (
			<RobotImage css={robotsImgStyles} alt={intl.formatMessage(messages.errorImageAltText)} />
		);
	}

	render() {
		const { hideFallbackUI } = this.props;
		const { hasError } = this.state;

		if (hasError && hideFallbackUI) {
			return <></>;
		}

		if (hasError) {
			return (
				<div css={errorBoundaryBoxStyles} data-testid="error-boundary">
					<div css={errorContentStyles}>
						{this.renderErrorImage()}
						<Box xcss={errorHeadingStyles}>
							<Heading size="medium">{this.renderErrorHeading()}</Heading>
						</Box>
						{this.renderErrorBody()}
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

const RobotImage: React.ComponentType<React.ComponentProps<'img'>> = (props) => {
	const [imgSrc, setImgSrc] = useState<string | undefined>();
	useEffect(() => {
		import(
			/* webpackChunkName: "@ak-switcher-chunk-robot-error-img" */ '../../assets/robot-error'
		).then((src) => setImgSrc(src.default));
	}, []);
	// eslint-disable-next-line jsx-a11y/alt-text
	return <img {...props} src={imgSrc} />;
};

export default withAnalyticsEvents()(injectIntl(ErrorBoundary));
